import { Stack } from "@mui/material";
import { formModes } from "EmbeddedForm.hook";
import { useLocationsAndProvidersOptions } from "hooks/useLocationsAndProvidersOptions";
import { ReactComponent as SuccessIcon } from "images/success.svg";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { toastifyConfig } from "../ReferralForm/ReferralForm.common";
import { EmbeddedReferralDetailsForm } from "./EmbeddedReferralDetailsForm";
import { useEffect } from "react";
import { defaultPractice, defaultSender } from "EmbeddedForm";
import {
  mapNewPracticeWithNewSenderReferralPayload,
  replaceNullableValuesWithNull,
} from "utils";

/** @type {import("types").NewReferralEmbeddedFormData} */
const defaulPatientData = {
  dashboardAccessToken: "",
  providerId: "",
  externalForm: "",
  patientName: "",
  patientPhone: "",
  patientDob: "",
  patientLocation: "",
  gender: "",
  urgency: "",
  reason: "",
  locationId: "",
};

export const CreateReferral = ({
  formCode,
  originator,
  providers,
  setProviders,
  recipient,
  locations,
  setMode,
  setLoading,
  setError,
  loading,
  reloadAuthData,
  hanldeLogout,
  loginState,
  setLoginState,
}) => {
  const {
    control,
    setValue,
    getValues,
    reset,
    watch,
    trigger,
    setError: setFormError,
    clearErrors,
  } = useForm({
    defaultValues: {
      ...defaultPractice,
      ...defaultSender,
      ...defaulPatientData,
      firstName: originator?.firstName ?? "",
      lastName: originator?.lastName ?? "",
      practiceId: originator?.organizationId ?? "",
      practiceName: originator?.practiceName ?? "",
      senderId: originator?.id ?? "",
      dashboardAccessToken: originator?.dashboardAccessToken,
      externalForm: formCode,
    },
  });

  const handleResetAndBack = () => {
    reset({
      ...defaultPractice,
      ...defaultSender,
      ...defaulPatientData,
      firstName: originator?.firstName ?? "",
      lastName: originator?.lastName ?? "",
      practiceId: originator?.organizationId ?? "",
      practiceName: originator?.practiceName ?? "",
      senderId: originator?.id ?? "",
      dashboardAccessToken: originator?.dashboardAccessToken,
      externalForm: formCode,
    });
    handleClose();
  };

  const handleClose = () => setMode(formModes.START);

  const showSuccessNotification = () =>
    toast(
      () => (
        <Stack direction={"row"} gap={1}>
          <SuccessIcon />
          <Stack>
            Referral sent.
            <div className="text-sm">
              Thank you for reaching out to {recipient?.name}. They will contact
              you shortly.
            </div>
          </Stack>
        </Stack>
      ),
      toastifyConfig
    );

  const onSubmit = async (values) => {
    const isNewSender = !values?.dashboardAccessToken;

    const preparedValues = isNewSender
      ? mapNewPracticeWithNewSenderReferralPayload(values)
      : replaceNullableValuesWithNull(values);

    try {
      setLoading(true);
      const response = await fetch(
        isNewSender
          ? `${process.env.REACT_APP_API_V2_URL}/referral/referral/external/new-sender`
          : `${process.env.REACT_APP_API_V2_URL}/referral/referral/external`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(preparedValues),
        }
      );
      const result = await response.json();

      if (result?.error || result?.message) {
        setError(result?.message || result?.error || "Something went wrong");
        return;
      }

      showSuccessNotification();
      handleClose();
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setLoading(false);
    }
  };

  const {
    isMoreThanOneProvider,
    isLocationsPresent,
    locationOptions,
    providerOptions,
  } = useLocationsAndProvidersOptions({
    locations,
    providers,
    setValue,
  });

  useEffect(() => {
    setValue("practiceId", originator?.organizationId ?? "", {
      shouldDirty: false,
    });
    setValue("practiceName", originator?.practiceName ?? "", {
      shouldDirty: false,
    });
    setValue("senderId", originator?.id ?? "", { shouldDirty: false });
    setValue("firstName", originator?.firstName ?? "", { shouldDirty: false });
    setValue("lastName", originator?.lastName ?? "", { shouldDirty: false });
    setValue("dashboardAccessToken", originator?.dashboardAccessToken ?? "", {
      shouldDirty: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originator]);

  return (
    <EmbeddedReferralDetailsForm
      originator={originator}
      control={control}
      handleResetAndBack={handleResetAndBack}
      recipient={recipient}
      loading={loading}
      watch={watch}
      handleClose={handleClose}
      onQuestionSubmit={onSubmit}
      isButtonEnabled={!loading}
      getValues={getValues}
      setValue={setValue}
      providerOptions={providerOptions}
      locationOptions={locationOptions}
      trigger={trigger}
      setLoading={setLoading}
      reset={reset}
      showSuccessNotification={showSuccessNotification}
      dashboardAccessToken={originator?.dashboardAccessToken}
      isLocationsPresent={isLocationsPresent}
      isMoreThanOneProvider={isMoreThanOneProvider}
      setError={setError}
      setFormError={setFormError}
      clearErrors={clearErrors}
      reloadAuthData={reloadAuthData}
      loginState={loginState}
      setLoginState={setLoginState}
      hanldeLogout={hanldeLogout}
      setProviders={setProviders}
    />
  );
};
